import type { NuxtApp } from '#app'
import { effectScope } from 'vue'
import { useCoreLogger } from '#imports'

interface PrefetchState {
    /** Set while declarations run: the promises they started. */
    started?: Promise<unknown>[]
    held: Map<string, { promise: Promise<unknown>, controller: AbortController }>
    /** What held requests returned, kept after a claimant took them, for later prefetches to read. */
    returned: Map<string, unknown>
}

declare module '#app' {
    interface NuxtApp {
        /** Lives and dies with the app, which the server creates per request, like `_asyncData`. Not `_prefetch`: Nuxt 4.6 keeps its link-prefetch scheduler there. */
        _corePrefetch?: PrefetchState
    }
}

function stateOf(nuxtApp: NuxtApp): PrefetchState {
    return nuxtApp._corePrefetch ??= { held: new Map(), returned: new Map() }
}

/** Runs `declare` in prefetch mode; see `useQueryClient().prefetchQueries`. Returns the requests it started. */
export function runPrefetch(nuxtApp: NuxtApp, declare: () => void): Promise<unknown>[] {
    const state = stateOf(nuxtApp)
    const started: Promise<unknown>[] = []
    // Stopped straight away, so nothing a declaration creates outlives it, and a shared composable
    // first reached here is released rather than keeping this call's stand-in queries.
    const scope = effectScope(true)

    state.started = started

    try {
        scope.run(() => nuxtApp.runWithContext(declare))
    }
    catch (error) {
        // A prefetch is only a head start; the page's own query still runs.
        nuxtApp.runWithContext(() => useCoreLogger().withTag('prefetch').warn('a declaration threw', error))
    }
    finally {
        state.started = undefined
        scope.stop()
    }

    return started
}

export function isPrefetching(nuxtApp: NuxtApp): boolean {
    return !!nuxtApp._corePrefetch?.started
}

export function holdPrefetch(nuxtApp: NuxtApp, key: string, request: (signal: AbortSignal) => Promise<unknown>) {
    const { held, returned, started } = stateOf(nuxtApp)

    if (held.has(key)) return

    const controller = new AbortController()
    const promise = Promise.resolve(request(controller.signal))

    // Registered first, so every later callback on the request already sees its result. Also keeps
    // an unclaimed failure from surfacing as an unhandled rejection; a claimant still sees it.
    promise.then(value => returned.set(key, value ?? null), () => {})
    held.set(key, { promise, controller })
    started?.push(promise)
}

/** Hands the held request for `key` to its first claimant. */
export function takePrefetch(nuxtApp: NuxtApp, key: string): Promise<unknown> | undefined {
    const held = nuxtApp._corePrefetch?.held
    const promise = held?.get(key)?.promise

    held?.delete(key)

    return promise
}

export function prefetchResult(nuxtApp: NuxtApp, key: string): unknown {
    return nuxtApp._corePrefetch?.returned.get(key)
}

/**
 * Replaced, not emptied: a request still in flight writes into the state it was held in. Aborts
 * the requests no query took over, and returns their keys.
 */
export function clearPrefetches(nuxtApp: NuxtApp): string[] {
    const held = nuxtApp._corePrefetch?.held ?? new Map()

    nuxtApp._corePrefetch = undefined

    for (const { controller } of held.values()) controller.abort()

    return [...held.keys()]
}

/** Returns whether the prefetch state is still the one current now, i.e. no `clearPrefetches` ran since. */
export function capturePrefetchState(nuxtApp: NuxtApp): () => boolean {
    const state = stateOf(nuxtApp)

    return () => nuxtApp._corePrefetch === state
}
